import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

export const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

const AuthContext = createContext(null);

export function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function msUntilExpiry(expiresIn) {
  if (!expiresIn) return 900_000;
  const ms = expiresIn <= 86_400 ? expiresIn * 1_000 : expiresIn;
  return Math.max(ms - 60_000, 30_000);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const accessTokenRef = useRef(null);
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    const id = api.interceptors.request.use((cfg) => {
      if (accessTokenRef.current) cfg.headers.Authorization = `Bearer ${accessTokenRef.current}`;
      return cfg;
    });
    return () => api.interceptors.request.eject(id);
  }, []);

  const silentRefresh = useCallback(async () => {
    try {
      const { data } = await api.post('/auth/refresh');
      setAccessToken(data.accessToken);
      setUser(data.user);
      const delay = msUntilExpiry(data.expiresIn);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(silentRefresh, delay);
    } catch {
      setAccessToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    silentRefresh();
    return () => clearTimeout(timerRef.current);
  }, [silentRefresh]);

  const scheduleRefresh = useCallback((expiresIn) => {
    const delay = msUntilExpiry(expiresIn);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(silentRefresh, delay);
  }, [silentRefresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setAccessToken(data.accessToken);
    setUser(data.user);
    scheduleRefresh(data.expiresIn);
    return data.user;
  }, [scheduleRefresh]);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post('/auth/register', { name, email, password });
    setAccessToken(data.accessToken);
    setUser(data.user);
    scheduleRefresh(data.expiresIn);
    return data.user;
  }, [scheduleRefresh]);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ } finally {
      clearTimeout(timerRef.current);
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const roleLower = (user?.role || '').toLowerCase();

  return (
    <AuthContext.Provider value={{
      user, accessToken, loading, api,
      login, register, logout,
      isAuthenticated: !!user,
      role: roleLower,
      isAdmin: roleLower === 'admin',
      isEditor: roleLower === 'editor' || roleLower === 'admin',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>');
  return ctx;
}

export default AuthContext;